import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    deleteScheduledMessage,
    fitScheduleDate,
    maxScheduleDate,
    minScheduleDate,
    scheduleText,
    scheduledErrorText,
    sendScheduledNow,
    toLocalInputValue,
    updateScheduledMessage,
} from "../../utils/scheduledMessagesApi";

/* Окна чата: время отложенной отправки, список отложенных, удаление. */

// Окно открывается изнутри сообщения: нажатия в нём не должны доходить до
// чата (выделение, меню по правой кнопке).
const stopEvent = (event) => event.stopPropagation();

/** Общее окно чата: заголовок, содержимое, кнопки внизу. Esc и нажатие мимо закрывают. */
export function ChatDialog({ title, onClose, children, footer = null, wide = false, busy = false }) {
    useEffect(() => {
        const onKey = (event) => {
            if (event.key === "Escape" && !busy) onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [busy, onClose]);

    if (typeof document === "undefined") return null;

    return createPortal(
        <div
            className="chat-dialog-AO"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onMouseDown={(event) => {
                event.stopPropagation();
                if (event.target === event.currentTarget && !busy) onClose?.();
            }}
            onClick={stopEvent}
            onDoubleClick={stopEvent}
            onContextMenu={stopEvent}
            onPointerDown={stopEvent}
            onPointerMove={stopEvent}
            onPointerUp={stopEvent}
            onPointerCancel={stopEvent}
            onTouchStart={stopEvent}
            onDragStart={stopEvent}
            onKeyDown={stopEvent}
        >
            <div className={`chat-dialog-AO__panel ${wide ? "is-wide" : ""}`}>
                <div className="chat-dialog-AO__head">
                    <h3>{title}</h3>
                    <button type="button" onClick={() => !busy && onClose?.()} aria-label="Закрыть">✕</button>
                </div>
                <div className="chat-dialog-AO__body">{children}</div>
                {footer ? <div className="chat-dialog-AO__foot">{footer}</div> : null}
            </div>
        </div>,
        document.body
    );
}

const atTime = (base, days, hours, minutes) => (
    new Date(base.getFullYear(), base.getMonth(), base.getDate() + days, hours, minutes, 0, 0)
);

/**
 * Когда отправить: дата и время с точностью до минуты. Раньше чем через
 * минуту и дальше чем через месяц выбрать нельзя — поле само не даст.
 */
export function ScheduleDialog({ title = "Отправить позже", confirmLabel = "Отложить", initial = null, onClose, onConfirm }) {
    const [value, setValue] = useState(() => toLocalInputValue(fitScheduleDate(initial || minScheduleDate())));
    // Граница «не раньше» сдвигается со временем.
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 15000);
        return () => window.clearInterval(timer);
    }, []);

    const min = minScheduleDate(now);
    const max = maxScheduleDate(new Date(now));
    const chosen = new Date(value);
    const valid = !Number.isNaN(chosen.getTime());
    const tooEarly = valid && chosen.getTime() < min.getTime();
    const tooLate = valid && chosen.getTime() > max.getTime();

    const today = new Date(now);
    const quick = [
        { label: "Через час", date: new Date(Math.ceil((now + 60 * 60 * 1000) / 60000) * 60000) },
        { label: "Сегодня в 18:00", date: atTime(today, 0, 18, 0) },
        { label: "Завтра в 09:00", date: atTime(today, 1, 9, 0) },
        { label: "Через неделю", date: atTime(today, 7, 9, 0) },
    ].filter((item) => item.date.getTime() >= min.getTime() && item.date.getTime() <= max.getTime());

    const confirm = () => {
        // Пока выбирали, минута могла пройти — тогда отправим через минуту, без ошибки.
        onConfirm?.(fitScheduleDate(valid ? chosen : min));
    };

    return (
        <ChatDialog
            title={title}
            onClose={onClose}
            footer={(
                <>
                    <button type="button" onClick={onClose}>Отмена</button>
                    <button type="button" className="is-primary" onClick={confirm}>{confirmLabel}</button>
                </>
            )}
        >
            <div className="chat-schedule-AO">
                <div className="chat-schedule-AO__result">
                    {valid ? scheduleText(fitScheduleDate(chosen)) : "Выберите дату и время"}
                </div>

                <div className="chat-schedule-AO__chips">
                    {quick.map((item) => (
                        <button
                            key={item.label}
                            type="button"
                            className={valid && Math.abs(chosen.getTime() - item.date.getTime()) < 60000 ? "is-active" : ""}
                            onClick={() => setValue(toLocalInputValue(item.date))}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                <label className="chat-schedule-AO__field">
                    <span>Дата и время</span>
                    <input
                        type="datetime-local"
                        value={value}
                        min={toLocalInputValue(min)}
                        max={toLocalInputValue(max)}
                        onChange={(event) => setValue(event.target.value)}
                        onBlur={() => {
                            // Ввели прошедшее или слишком далёкое — ставим ближайшее допустимое.
                            if (!valid || tooEarly || tooLate) setValue(toLocalInputValue(fitScheduleDate(valid ? chosen : min)));
                        }}
                    />
                </label>

                {tooEarly ? <p className="chat-schedule-AO__hint">Это время уже прошло — отправим через минуту.</p> : null}
                {tooLate ? <p className="chat-schedule-AO__hint">Отложить можно не больше чем на месяц вперёд.</p> : null}
                <p className="chat-dialog-AO__hint">
                    Сообщение уйдёт само в назначенное время, даже если компьютер выключен. До этого его видите только вы.
                </p>
            </div>
        </ChatDialog>
    );
}

const fileLabel = (item) => {
    if (item?.messageKind === "video_note") return "Кружок";
    const kind = item?.file?.kind;
    if (kind === "image") return "Фото";
    if (kind === "video") return "Видео";
    if (kind === "voice") return "Голосовое сообщение";
    return item?.file?.name || "Файл";
};

const fileIcon = (item) => {
    if (item?.messageKind === "video_note") return "◉";
    const kind = item?.file?.kind;
    if (kind === "image") return "▧";
    if (kind === "video") return "▶";
    if (kind === "voice") return "🎙";
    return "▤";
};

/**
 * Отложенные сообщения чата: что и когда уйдёт. Каждое можно отправить
 * сразу, перенести или удалить. Не ушедшее вовремя остаётся с причиной.
 */
export function ScheduledMessagesDialog({ items = [], onClose, onChanged, onNotify }) {
    const [busyId, setBusyId] = useState("");
    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);
    const [error, setError] = useState("");
    const [timeFor, setTimeFor] = useState(null);

    // Отложенных не осталось — окно больше не нужно.
    useEffect(() => {
        if (!items.length && !timeFor) onClose?.();
    }, [items.length, onClose, timeFor]);

    const run = async (item, action, doneText) => {
        if (busyId) return;
        setBusyId(item._id);
        setError("");
        try {
            await action();
            await onChanged?.();
            onNotify?.(doneText);
        } catch (actionError) {
            setError(scheduledErrorText(actionError));
            onChanged?.();
        } finally {
            if (aliveRef.current) setBusyId("");
        }
    };

    if (timeFor) {
        return (
            <ScheduleDialog
                title="Когда отправить"
                confirmLabel="Сохранить"
                initial={new Date(timeFor.sendAt)}
                onClose={() => setTimeFor(null)}
                onConfirm={(sendAt) => {
                    const item = timeFor;
                    setTimeFor(null);
                    run(item, () => updateScheduledMessage(item._id, { sendAt }), `Отправится: ${scheduleText(sendAt).toLowerCase()}`);
                }}
            />
        );
    }

    return (
        <ChatDialog title="Отложенные сообщения" onClose={onClose} wide>
            {error ? <div className="chat-dialog-AO__error" role="alert">{error}</div> : null}

            <div className="chat-scheduled-AO">
                {items.map((item) => {
                    const failed = item.status === "failed";
                    const busy = busyId === item._id;
                    const text = String(item.text || "").trim();

                    return (
                        <div key={item._id} className={`chat-scheduled-AO__item ${failed ? "is-failed" : ""}`}>
                            <div className="chat-scheduled-AO__when">
                                <span>{failed ? "Не отправлено" : scheduleText(item.sendAt)}</span>
                                {busy ? <i className="chat-album-AO__spinner" /> : null}
                            </div>

                            {failed ? (
                                <p className="chat-scheduled-AO__error">
                                    {item.error || "Отправить не получилось."} Было назначено: {scheduleText(item.sendAt).toLowerCase()}.
                                </p>
                            ) : null}

                            {item.file ? (
                                <div className="chat-scheduled-AO__file">
                                    <span>{fileIcon(item)}</span>
                                    <b>{fileLabel(item)}</b>
                                </div>
                            ) : null}

                            {text ? <p className="chat-scheduled-AO__text">{text}</p> : null}

                            <div className="chat-scheduled-AO__actions">
                                <button type="button" disabled={!!busyId} onClick={() => run(item, () => sendScheduledNow(item._id), "Отправлено")}>
                                    ➤ Отправить сейчас
                                </button>
                                <button type="button" disabled={!!busyId} onClick={() => setTimeFor(item)}>
                                    ◷ Изменить время
                                </button>
                                <button type="button" className="is-danger" disabled={!!busyId} onClick={() => run(item, () => deleteScheduledMessage(item._id), "Отложенное сообщение удалено")}>
                                    Удалить
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </ChatDialog>
    );
}

/**
 * Удалить сообщения: у себя (у остальных останутся) или у всех.
 * «У всех» — только для своих сообщений.
 */
export function DeleteMessagesDialog({ messages = [], meId = "", onClose, onConfirm }) {
    const [busy, setBusy] = useState(false);
    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);
    const allMine = useMemo(() => messages.every((message) => (
        String(message?.sender?._id || message?.sender || "") === String(meId)
    )), [messages, meId]);
    const count = messages.length;

    const run = async (scope) => {
        if (busy) return;
        setBusy(true);
        try {
            await onConfirm?.(scope);
        } finally {
            // После удаления окно уже закрыто.
            if (aliveRef.current) setBusy(false);
        }
    };

    return (
        <ChatDialog
            title={count > 1 ? `Удалить сообщения: ${count}` : "Удалить сообщение"}
            onClose={onClose}
            busy={busy}
            footer={(
                <>
                    <button type="button" onClick={onClose} disabled={busy}>Отмена</button>
                    <button type="button" onClick={() => run("me")} disabled={busy}>Удалить у себя</button>
                    {allMine ? (
                        <button type="button" className="is-primary is-danger" onClick={() => run("all")} disabled={busy}>
                            Удалить у всех
                        </button>
                    ) : null}
                </>
            )}
        >
            <p className="chat-dialog-AO__hint">
                «У себя» — сообщение пропадёт только у вас. {allMine ? "«У всех» — исчезнет у всех участников чата." : ""}
            </p>
        </ChatDialog>
    );
}
